<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import type { Product } from '@bf/domain'
import { useShowroomStore } from '../store/showroom'
import EngineerSupplementPanel from '../components/EngineerSupplementPanel.vue'
import {
  controllerApplicationNotes,
  controllerCatalogLabels,
  controllerSourceLabels,
  controllerSourceNotes,
  controllerUnitCatalogs,
  type ControllerFamilyId
} from '../data/controller-units'

const store = useShowroomStore()
const controllerIds = [
  'pcs-200-controller-family',
  'fcs-200-controller-family',
  'g3-dm2-controller-family'
] as const

const controllers = computed(() =>
  controllerIds
    .map((id) => store.getEntity(id))
    .filter(
      (entity): entity is Product =>
        entity?.contentType === 'product' &&
        entity.productCategory === 'ddc-controller'
    )
)

const selectedControllerId = ref<(typeof controllerIds)[number]>(
  'pcs-200-controller-family'
)
const selectedUnitId = ref(controllerUnitCatalogs['pcs-200-controller-family'][0].id)
const currentFamilyId = computed(() => selectedControllerId.value as ControllerFamilyId)
const currentUnits = computed(() => controllerUnitCatalogs[currentFamilyId.value])
const selectedUnit = computed(() =>
  currentUnits.value.find((unit) => unit.id === selectedUnitId.value) ?? currentUnits.value[0]
)
const selectedUnitImage = computed(() => store.getAsset(selectedUnit.value.imageId ?? ''))
const selectedUnitManual = computed(() => store.getAsset(selectedUnit.value.manualId ?? ''))
const pscImageViewports: Record<string, { width: number; height: number; viewBox: string }> = {
  'pac202-8644': { width: 1125, height: 586, viewBox: '17 45 1076 504' },
  'pac206-128': { width: 1781, height: 1781, viewBox: '137 341 1482 1099' },
  'pac208-2416': { width: 647, height: 647, viewBox: '18 177 615 279' },
  'pac208-8644': { width: 647, height: 647, viewBox: '18 180 612 288' },
  'pec202-8644': { width: 3460, height: 1623, viewBox: '0 0 3460 1623' },
  'pec208-8644': { width: 3460, height: 1623, viewBox: '0 0 3460 1623' },
  'dio-12': { width: 1583, height: 1583, viewBox: '120 250 1322 1090' },
  'ai-8': { width: 1676, height: 1676, viewBox: '105 296 1399 1090' },
  'ao-8': { width: 1676, height: 1676, viewBox: '132 304 1348 1085' },
  'di-16': { width: 1583, height: 1583, viewBox: '90 274 1339 1090' }
}
function unitImageViewport(unitId: string) {
  return currentFamilyId.value === 'pcs-200-controller-family'
    ? pscImageViewports[unitId]
    : undefined
}
const selectedController = computed(
  () =>
    controllers.value.find(
      (controller) => controller.id === selectedControllerId.value
    ) ??
    controllers.value[0] ??
    null
)
watch(
  selectedControllerId,
  () => {
    selectedUnitId.value = currentUnits.value[0].id
  },
)

function selectController(id: (typeof controllerIds)[number]) {
  selectedControllerId.value = id
}
</script>

<template>
  <section class="reference-page-heading">
    <p class="eyebrow">CONTROLLER &amp; I/O</p>
    <h2>控制器与 I/O 模块</h2>
    <p>三个控制系统均按独立产品整理，可逐项查看控制器、通讯模块、耦合器和 I/O 的图片、参数与优势。</p>
  </section>

  <div class="series-tabs" role="tablist" aria-label="控制器系列">
    <button
      v-for="controller in controllers"
      :key="controller.id"
      type="button"
      role="tab"
      :class="{ active: selectedController?.id === controller.id }"
      :aria-selected="selectedController?.id === controller.id"
      @click="selectController(controller.id as (typeof controllerIds)[number])"
    >
      <i aria-hidden="true"></i>
      {{ controller.title }}
    </button>
    <span>PSC200网站与参数表 · FCS200使用手册 · G3/DM2产品介绍</span>
  </div>

  <div v-if="selectedController" class="controller-workbench">
    <section class="psc-unit-catalog psc-unit-browser">
      <header>
        <div>
          <span>{{ selectedController.title }} 独立产品</span>
          <h3>{{ controllerCatalogLabels[currentFamilyId] }}</h3>
        </div>
        <small>点击型号查看各自介绍</small>
      </header>
      <div class="psc-unit-grid">
        <button
          v-for="unit in currentUnits"
          :key="unit.id"
          type="button"
          :class="{ active: selectedUnit.id === unit.id }"
          :aria-label="`查看 ${unit.model} 独立产品介绍`"
          @click="selectedUnitId = unit.id"
        >
          <div v-if="unit.imageId && store.getAsset(unit.imageId)" class="psc-unit-image-frame">
            <svg v-if="unitImageViewport(unit.id)" :viewBox="unitImageViewport(unit.id)!.viewBox" role="img" :aria-label="`${unit.model} 产品图`">
              <image :href="store.mediaUrl(store.getAsset(unit.imageId)!)" x="0" y="0" :width="unitImageViewport(unit.id)!.width" :height="unitImageViewport(unit.id)!.height" preserveAspectRatio="xMidYMid meet" />
            </svg>
            <img v-else :src="store.mediaUrl(store.getAsset(unit.imageId)!)" :alt="`${unit.model} 产品图`" />
          </div>
          <span>{{ unit.kind }}</span>
          <strong>{{ unit.model }}</strong>
          <small>{{ unit.summary }}</small>
        </button>
      </div>
    </section>

    <aside class="controller-data-panel psc-unit-side-panel">
      <div class="panel-status"><span><i></i>独立产品</span><b>{{ controllerSourceLabels[currentFamilyId] }}</b></div>
      <div class="psc-side-image">
        <svg v-if="selectedUnitImage && unitImageViewport(selectedUnit.id)" :viewBox="unitImageViewport(selectedUnit.id)!.viewBox" role="img" :aria-label="`${selectedUnit.model} 产品图`">
          <image :href="store.mediaUrl(selectedUnitImage)" x="0" y="0" :width="unitImageViewport(selectedUnit.id)!.width" :height="unitImageViewport(selectedUnit.id)!.height" preserveAspectRatio="xMidYMid meet" />
        </svg>
        <img v-else-if="selectedUnitImage" :src="store.mediaUrl(selectedUnitImage)" :alt="`${selectedUnit.model} 产品图`" />
        <div v-else class="media-fallback"><strong>BF</strong><span>产品图片待接入</span></div>
      </div>
      <p class="controller-application-note"><strong>适用场景</strong>{{ controllerApplicationNotes[currentFamilyId] }}</p>
      <h3>{{ selectedUnit.model }}</h3>
      <p>{{ selectedUnit.summary }}</p>
      <h4 class="psc-website-parameter-title">设备参数</h4>
      <dl>
        <div><dt>所属系列</dt><dd>{{ selectedController.title }}</dd></div>
        <div><dt>独立型号</dt><dd>{{ selectedUnit.model }}</dd></div>
        <div v-for="parameter in selectedUnit.parameters" :key="parameter[0]">
          <dt>{{ parameter[0] }}</dt><dd>{{ parameter[1] }}</dd>
        </div>
      </dl>
      <h4 class="psc-website-parameter-title">产品优势</h4>
      <div class="controller-highlights psc-unit-highlights">
        <span v-for="item in selectedUnit.advantages ?? selectedController.highlights" :key="item">{{ item }}</span>
      </div>
      <a v-if="selectedUnitManual" class="psc-manual-link" :href="store.mediaUrl(selectedUnitManual)" target="_blank" rel="noopener">查看 {{ selectedUnit.model }} 产品说明书</a>
      <p class="psc-source-note">{{ controllerSourceNotes[currentFamilyId] }}</p>
      <div class="panel-actions">
        <button class="button button-primary" type="button" @click="store.addToDraft(selectedController)">加入项目方案</button>
        <RouterLink class="button button-secondary" :to="{ name: 'item', params: { id: selectedController.id } }">查看系列资料</RouterLink>
        <RouterLink class="button button-ghost" :to="{ name: 'catalog', params: { section: 'hardware' } }">硬件产品库</RouterLink>
      </div>
      <EngineerSupplementPanel :product-key="`${currentFamilyId}:${selectedUnit.id}`" />
    </aside>
  </div>

  <section class="showcase-fact-grid" aria-label="控制器展示说明">
    <article>
      <span>01</span>
      <h3>真实产品图</h3>
      <p>用已提供的控制器和模块图片替换原概念占位图。</p>
    </article>
    <article>
      <span>02</span>
      <h3>系列切换</h3>
      <p>PSC200、FCS200、G3Smart / DM2 共用同一交互结构。</p>
    </article>
    <article>
      <span>03</span>
      <h3>型号可追溯</h3>
      <p>PSC200 型号与参数来自源创智控产品目录；其他系列按现有资料追溯。</p>
    </article>
    <article>
      <span>04</span>
      <h3>工程复核</h3>
      <p>技术参数、供电、通讯和 I/O 配置仍以正式选型资料为准。</p>
    </article>
  </section>
</template>
